<template>
    <div v-if="nav" ref="navbarRef" class="barometer-navbar">
        <div class="barometer-navbar__header">
            <button @click="navOpen = !navOpen">
                {{ t('barometer.nav.title') }}
                <VJoyIcon name="chevron-down" size="xxsmall" />
            </button>
        </div>
        <nav class="barometer-navbar__menu" :class="{'barometer-navbar__menu--open': navOpen}">
            <ul>
                <li v-for="navItem in nav" :key="navItem.id">
                    <NuxtLink :to="$withLocalePrefix(navItem.url!)">
                        {{ navItem.label }}
                        <VJoyIcon v-if="navItem.nav?.length" name="chevron-right" size="xxsmall" />
                    </NuxtLink>

                    <ul v-if="navItem.nav?.length">
                        <li v-for="subNavItem in navItem.nav" :key="subNavItem.id">
                            <NuxtLink :to="$withLocalePrefix(subNavItem.url!)">
                                {{ subNavItem.label }}
                                <VJoyIcon v-if="subNavItem.nav?.length" name="chevron-right" size="xxsmall" />
                            </NuxtLink>
                            <ul v-if="subNavItem.nav?.length">
                                <li v-for="subsubNavItem in subNavItem.nav" :key="subsubNavItem.id">
                                    <NuxtLink :to="$withLocalePrefix(subsubNavItem.url!)">{{ subsubNavItem.label }}</NuxtLink>
                                </li>
                            </ul>
                        </li>
                    </ul>
                </li>
            </ul>
        </nav>
    </div>
</template>

<script setup lang="ts">
    import {ref, useTemplateRef} from 'vue';
    import {onClickOutside} from '@vueuse/core';
    import {useTranslation} from '#imports';
    import {VJoyIcon} from '@maltjoy/core-vue';
    import {type Nav} from '@api';

    defineProps<{nav?: Nav[]}>();

    const {t} = useTranslation();

    const navbarRef = useTemplateRef('navbarRef');
    const navOpen = ref(false);

    onClickOutside(navbarRef, () => {
        navOpen.value = false;
    });
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    $navbar-height: var(--joy-core-spacing-9);
    $nav-item-padding: var(--joy-core-spacing-4) var(--joy-core-spacing-4);

    .barometer-navbar {
        position: sticky;
        z-index: var(--joy-core-z-index-fixed-content);
        user-select: none;
        background-color: var(--joy-color-background-default);
        top: 50px;

        @include mq.screen_md(min) {
            top: 72px;
        }

        &__header {
            position: relative;
            z-index: var(--joy-core-z-index-fixed-content);
            box-shadow: var(--joy-core-elevation-1);
        }

        &__menu {
            position: absolute;
            top: 100%;
            display: none;
            width: 100%;
            box-shadow: var(--joy-core-elevation-1);

            &--open {
                display: block !important;

                > ul {
                    display: block !important;
                }
            }
        }

        button {
            padding: $nav-item-padding;
            line-height: var(--joy-line-height-large);
            display: flex;
            align-items: center;
            gap: var(--joy-core-spacing-2);
        }

        ul {
            background-color: var(--joy-color-background-default);
            position: absolute;
            top: 0;
            list-style-type: none;
            display: none;

            > li > ul {
                left: 100%;
            }
        }

        li {
            min-width: 250px;
            max-width: 350px;
            background-color: var(--joy-color-background-default);
            border: 1px solid var(--joy-color-neutral-20);
            border-width: 1px 0;

            &:hover {
                background-color: var(--joy-color-neutral-20);

                > ul {
                    min-height: 100%;
                    display: block;
                    top: 0;
                }
            }
        }

        a,
        a:visited {
            display: flex;
            align-items: center;
            justify-content: space-between;
            color: var(--joy-color-neutral-60);
            padding: $nav-item-padding;
        }
    }
</style>
